import React, {
  useContext, useState, useRef, useEffect,
} from 'react';
import PropTypes from 'prop-types';
import moment from 'moment-timezone';
import {
  Modal, Box, Button, FormControl, InputLabel, Select, MenuItem,
  FormControlLabel, Switch,
} from '@mui/material';
import { CSVLink } from 'react-csv';
import { sendRequest } from '../../api/shootAPI';
import API from '../../api/endpoints';
import { UserContext } from '../../contexts/User';
import DateRangePicker from '../../components/dateRangePicker';
import { getDatesInRange, convertSecToTimeFormat } from '../../utils/time';
import InfoIconSVG from '../../images/infoIcon.svg';
import TeamSelect from '../userDashboard/TeamSelect';

function ExportAllModal({
  closeModal, teams, selectedTeamId, setSelectedTeamId, selectedTimezone,
}) {
  const [date, setDate] = useState([new Date(), new Date()]);
  const csvAnchor = useRef(null);
  const [csvData, setCsvData] = useState([]);
  const [isExporting, setIsExporting] = useState(false);
  const { activeWorkspace } = useContext(UserContext);
  const [selectedMemberId, setSelectedMemberId] = useState('all');
  const [availableMembers, setAvailableMembers] = useState([]);
  const [isLoadingMembers, setIsLoadingMembers] = useState(false);
  const [includeActivity, setIncludeActivity] = useState(false);

  const handleExportAll = async () => {
    setIsExporting(true);
    const [startDate, endDate] = date;
    const dates = getDatesInRange(startDate, endDate);
    const startOfDay = moment(dates[0]).startOf('day').tz(selectedTimezone).utc()
      .format('YYYY-MM-DD HH:mm:ss');
    const endOfDay = moment(dates.at(-1)).endOf('day').tz(selectedTimezone).utc()
      .format('YYYY-MM-DD HH:mm:ss');
    // Add first row: For the Period, Date Range
    const periodRow = [
      'For the Period',
      '',
      '',
      `${moment(startDate).format('YYYY-MM-DD')} to ${moment(endDate).format('YYYY-MM-DD')}`,
    ];
    // Static (member-level) column labels
    const staticLabels = [
      'Group by Member', 'Department', 'Designation', 'Location', 'Country',
      'Total Regular Hours', 'Total Time Off', 'Total Hours on Date', 'Average Activity',
    ];
    // When activity is included, each day uses two cells (time + activity %)
    // and the date is shown once with a blank cell above the activity column.
    // Otherwise each day is a single time column.
    const dateHeader = [];
    dates.forEach((day) => {
      const dateLabel = moment(day).format('YYYY-MM-DD');
      if (includeActivity) {
        dateHeader.push(dateLabel, '');
      } else {
        dateHeader.push(dateLabel);
      }
    });
    const csvRawData = [
      periodRow,
      [...staticLabels, ...dateHeader],
    ];
    const response = await sendRequest({
      method: 'get',
      timeout: 60000,
      url: API.WORKSPACE.membersActivityExport(activeWorkspace.id),
      params: {
        start: startOfDay,
        end: endOfDay,
        selectedTeamId,
        selectedMemberId: selectedMemberId !== 'all' ? selectedMemberId : undefined,
      },
    });
    if (response.success) {
      const mappedManualTimeEntries = {};
      if (response.data.manualEntries && response.data.manualEntries.length > 0) {
        response.data.manualEntries.forEach((entry) => {
          const { memberId } = entry;
          const entryDate = entry.date;
          const seconds = Number(entry.secondsElapsed || 0);

          if (!mappedManualTimeEntries[memberId]) {
            mappedManualTimeEntries[memberId] = {};
          }
          mappedManualTimeEntries[memberId][entryDate] = seconds;
        });
      }

      const mappedPTOEntries = {};
      if (response.data.ptoEntries && response.data.ptoEntries.length > 0) {
        response.data.ptoEntries.forEach((entry) => {
          const { memberId } = entry;
          const startDate = moment(entry.startDate).format('YYYY-MM-DD');
          const endDate = moment(entry.endDate).format('YYYY-MM-DD');
          const totalMinutes = Number(entry.totalHours || 0);

          const start = moment(startDate);
          const end = moment(endDate);
          const dayCount = end.diff(start, 'days') + 1;

          const minutesPerDay = totalMinutes / dayCount;
          const secondsPerDay = minutesPerDay * 60;

          const currentDate = moment(startDate);
          while (currentDate.isSameOrBefore(end)) {
            const dateString = currentDate.format('YYYY-MM-DD');

            if (!mappedPTOEntries[memberId]) {
              mappedPTOEntries[memberId] = {};
            }

            if (!mappedPTOEntries[memberId][dateString]) {
              mappedPTOEntries[memberId][dateString] = 0;
            }

            mappedPTOEntries[memberId][dateString] += secondsPerDay;
            currentDate.add(1, 'day');
          }
        });
      }

      const members = response.data?.members ?? [];
      members.forEach((member) => {
        const userName = member.user.firstName ? member.user.firstName.concat(' ', member.user.lastName) : '';
        // const csvRowData = [
        //   userName,
        //   member.department,
        //   member.designation,
        //   member.user.timezone,
        //   member.user.userLocation?.country || '',
        // ];
        const datesActivityColumns = [];
        let totalSecondsElapsed = 0;
        let totalActiveSeconds = 0;
        let totalManualTimeSeconds = 0;
        let totalPTOSeconds = 0;

        dates.forEach((day) => {
          const startDay = moment(day).tz(selectedTimezone).startOf('day');
          const endDay = moment(day).tz(selectedTimezone).endOf('day');
          const initialdateActivity = { secondsElapsed: 0, activeSeconds: 0 };
          const dateActivity = member.dailyActivityTz.reduce(
            (acc, activity) => {
              const compareDay = moment(activity.date).tz(selectedTimezone);
              if (compareDay.isBetween(startDay, endDay, null, '[]')) {
                return {
                  secondsElapsed: acc.secondsElapsed + activity.totalSeconds,
                  activeSeconds: acc.activeSeconds + activity.activeSeconds,
                };
              }
              return acc;
            },
            initialdateActivity,
          );

          const dayString = moment(day).format('YYYY-MM-DD');
          const manualTimeForDay = (mappedManualTimeEntries[member.id]
             && mappedManualTimeEntries[member.id][dayString]) || 0;

          const ptoTimeForDay = (mappedPTOEntries[member.id]
            && mappedPTOEntries[member.id][dayString]) || 0;

          totalSecondsElapsed += dateActivity.secondsElapsed + manualTimeForDay + ptoTimeForDay;
          totalActiveSeconds += dateActivity.activeSeconds;
          totalManualTimeSeconds += manualTimeForDay;
          totalPTOSeconds += ptoTimeForDay;

          const totalTimeForDay = dateActivity.secondsElapsed + manualTimeForDay + ptoTimeForDay;
          const formattedTime = moment.utc(totalTimeForDay * 1000).format('H:mm:ss');

          if (includeActivity) {
            // Per-day activity % is based on tracked time only (excludes manual / PTO)
            const dayActivityPercentage = dateActivity.secondsElapsed > 0
              ? `${((dateActivity.activeSeconds / dateActivity.secondsElapsed) * 100).toFixed(0)}%`
              : '0%';
            datesActivityColumns.push(formattedTime, dayActivityPercentage);
          } else {
            datesActivityColumns.push(formattedTime);
          }
        });

        // const totalSecondsElapsedFormatted = convertSecToTimeFormat(totalSecondsElapsed);
        const avgActivityPercentage = (((totalActiveSeconds
          / (totalSecondsElapsed - totalManualTimeSeconds - totalPTOSeconds)) || 0) * 100);
        const formattedAvgActivityPercentage = `${avgActivityPercentage.toFixed(0)}%`;

        // New calculations for Total Time Off, Total Hours on Date, and Total Regular Hours
        const totalTimeOffFormatted = convertSecToTimeFormat(totalPTOSeconds);
        const totalHoursOnDateFormatted = convertSecToTimeFormat(totalSecondsElapsed);
        const totalRegularHours = totalSecondsElapsed - totalPTOSeconds;
        const totalRegularHoursFormatted = convertSecToTimeFormat(totalRegularHours);

        csvRawData.push([ // Update the push to match the new columns order and calculation
          userName,
          member.department,
          member.designation,
          member.user.timezone,
          member.user.userLocation?.country || '',
          totalHoursOnDateFormatted,
          totalTimeOffFormatted,
          totalRegularHoursFormatted,
          formattedAvgActivityPercentage,
          ...datesActivityColumns,
        ]);
      });
      // Add 2 empty rows gap before grand total
      csvRawData.push([], []);
      // Calculate grand totals
      let grandTotalTimeOff = 0;
      let grandTotalHoursOnDate = 0;
      let grandTotalRegularHours = 0;
      let grandTotalActivitySum = 0;
      let grandTotalActivityCount = 0;
      members.forEach((member) => {
        let memberTotalSecondsElapsed = 0;
        let memberTotalPTOSeconds = 0;
        let memberTotalActiveSeconds = 0;
        let memberTotalManualTimeSeconds = 0;
        dates.forEach((day) => {
          const startDay = moment(day).tz(selectedTimezone).startOf('day');
          const endDay = moment(day).tz(selectedTimezone).endOf('day');
          const initialdateActivity = { secondsElapsed: 0, activeSeconds: 0 };
          const dateActivity = member.dailyActivityTz.reduce(
            (acc, activity) => {
              const compareDay = moment(activity.date).tz(selectedTimezone);
              if (compareDay.isBetween(startDay, endDay, null, '[]')) {
                return {
                  secondsElapsed: acc.secondsElapsed + activity.totalSeconds,
                  activeSeconds: acc.activeSeconds + activity.activeSeconds,
                };
              }
              return acc;
            },
            initialdateActivity,
          );
          const dayString = moment(day).format('YYYY-MM-DD');
          const manualTimeForDay = (mappedManualTimeEntries[member.id]
            && mappedManualTimeEntries[member.id][dayString]) || 0;
          const ptoTimeForDay = (mappedPTOEntries[member.id]
            && mappedPTOEntries[member.id][dayString]) || 0;
          memberTotalSecondsElapsed += dateActivity.secondsElapsed + manualTimeForDay + ptoTimeForDay;
          memberTotalPTOSeconds += ptoTimeForDay;
          memberTotalActiveSeconds += dateActivity.activeSeconds;
          memberTotalManualTimeSeconds += manualTimeForDay;
        });
        grandTotalTimeOff += memberTotalPTOSeconds;
        grandTotalHoursOnDate += memberTotalSecondsElapsed;
        grandTotalRegularHours += (memberTotalSecondsElapsed - memberTotalPTOSeconds);
        // Calculate activity for this member
        const denominator = memberTotalSecondsElapsed - memberTotalManualTimeSeconds - memberTotalPTOSeconds;
        if (denominator > 0) {
          grandTotalActivitySum += (memberTotalActiveSeconds / denominator) * 100;
          grandTotalActivityCount += 1;
        }
      });
      const formattedGrandTotalTimeOff = convertSecToTimeFormat(grandTotalTimeOff);
      const formattedGrandTotalHoursOnDate = convertSecToTimeFormat(grandTotalHoursOnDate);
      const formattedGrandTotalRegularHours = convertSecToTimeFormat(grandTotalRegularHours);
      // eslint-disable-next-line max-len
      const formattedGrandAvgActivity = grandTotalActivityCount > 0 ? `${(grandTotalActivitySum / grandTotalActivityCount).toFixed(0)}%` : '0%';
      csvRawData.push([
        'Grand Total', '', '', '', '',
        formattedGrandTotalHoursOnDate,
        formattedGrandTotalTimeOff,
        formattedGrandTotalRegularHours,
        formattedGrandAvgActivity,
      ]);
      setCsvData(csvRawData);
    }
    setIsExporting(false);
  };

  useEffect(() => {
    const fetchMembers = async () => {
      if (!activeWorkspace?.id) return;

      setIsLoadingMembers(true);
      try {
        const today = moment().format('YYYY-MM-DD');
        const response = await sendRequest({
          method: 'get',
          url: API.WORKSPACE.membersActivityExport(activeWorkspace.id),
          params: {
            start: `${today} 00:00:00`,
            end: `${today} 23:59:59`,
            selectedTeamId,
          },
        });

        if (response.success) {
          const members = response.data?.members ?? [];
          setAvailableMembers(members.map((member) => ({
            id: member.id,
            name: `${member.user.firstName || ''} ${member.user.lastName || ''}`.trim() || member.user.email,
            email: member.user.email,
          })));
        } else {
          console.error('Failed to fetch members:', response);
          setAvailableMembers([]);
        }
      } catch (error) {
        console.error('Error fetching members:', error);
        setAvailableMembers([]);
      }
      setIsLoadingMembers(false);
    };

    fetchMembers();
  }, [selectedTeamId, activeWorkspace?.id]);

  useEffect(() => {
    setSelectedMemberId('all');
  }, [selectedTeamId]);

  useEffect(() => {
    if (csvData.length > 0) {
      csvAnchor.current.link.click();
    }
  }, [csvData]);

  const [startDate, endDate] = date;
  const generateFileName = () => {
    const startFormatted = moment(startDate).subtract(1, 'day').format('MM-DD-YYYY');
    const endFormatted = moment(endDate).format('MM-DD-YYYY');

    let filename = `Activity_${startFormatted}_to_${endFormatted}`;

    if (selectedTeamId !== '0') {
      const selectedTeam = teams.find((team) => team.value?.toString() === selectedTeamId?.toString());
      if (selectedTeam) {
        filename += `_${selectedTeam.label.replace(/[^a-zA-Z0-9]/g, '')}`;
      }
    }

    if (selectedMemberId && selectedMemberId !== 'all') {
      const selectedMember = availableMembers.find(
        (member) => member.id?.toString() === selectedMemberId?.toString(),
      );
      if (selectedMember) {
        filename += `_${selectedMember.name.replace(/[^a-zA-Z0-9]/g, '')}`;
      }
    }

    return `${filename}.csv`;
  };

  return (
    <Modal
      open
      onClose={closeModal}
      className="flex-row align-c jstfy-c"
      data-testid="export-all-timesheets-modal"
    >
      <Box style={{
        width: 482,
        padding: 32,
        background: '#fff',
        borderRadius: 8,
      }}
      >
        <h5 className="m-b16 text-600" data-testid="modal-title">Export All Timesheets</h5>
        <div className="info-section" data-testid="info-section">
          <img src={InfoIconSVG} alt="info icon" />
          <p className="info-text">
            Select the timesheet date range you wish to export
          </p>
        </div>

        <div className="d-flex gap-2" style={{ flexDirection: 'column', width: '100%' }}>
          <div className="d-flex gap-2">
            <TeamSelect
              menuItems={teams}
              value={selectedTeamId}
              handleForm={(e) => setSelectedTeamId(e.target.value)}
            />
            <DateRangePicker
              value={date}
              onChange={(_date) => setDate(_date)}
              data-testid="date-range-picker"
            />
          </div>
          {}
          <div className="d-flex gap-2">
            <FormControl fullWidth size="small">
              <InputLabel id="member-select-label">Member</InputLabel>
              <Select
                labelId="member-select-label"
                id="member-select"
                value={selectedMemberId}
                label="Member"
                onChange={(e) => setSelectedMemberId(e.target.value)}
                disabled={isLoadingMembers}
                data-testid="member-select"
              >
                <MenuItem value="all">All Members</MenuItem>
                {availableMembers.map((member) => (
                  <MenuItem key={member.id} value={member.id}>
                    {member.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </div>
          <FormControlLabel
            control={(
              <Switch
                checked={includeActivity}
                onChange={(e) => setIncludeActivity(e.target.checked)}
                data-testid="include-activity-switch"
              />
            )}
            label="Include activity percentage"
          />
        </div>
        <div className="exportAllForm-btns" style={{ marginTop: 24 }}>
          <Button
            onClick={closeModal}
            color="secondary"
            sx={{ marginRight: 2 }}
            data-testid="cancel"
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleExportAll}
            disabled={isExporting}
            data-testid="export"
          >
            {isExporting ? 'Exporting...' : 'Export'}
          </Button>
          <CSVLink
            data={csvData}
            ref={csvAnchor}
            filename={generateFileName()}
          />
        </div>
      </Box>
    </Modal>
  );
}

ExportAllModal.propTypes = {
  closeModal: PropTypes.func.isRequired,
  // eslint-disable-next-line react/forbid-prop-types
  teams: PropTypes.arrayOf(PropTypes.object).isRequired,
  selectedTeamId: PropTypes.string.isRequired,
  setSelectedTeamId: PropTypes.func.isRequired,
  selectedTimezone: PropTypes.string.isRequired,
};

export default ExportAllModal;
